import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight, CheckCircle2, AlertTriangle, OctagonX,
  DoorOpen, Car, Users, Timer, TrendingUp,
} from "lucide-react";

const ROUTE_STATUS = {
  Clear:      { color: "hsl(var(--success))",     icon: CheckCircle2, accent: "sev-low" },
  Restricted: { color: "hsl(var(--warning))",     icon: AlertTriangle, accent: "sev-medium" },
  Blocked:    { color: "hsl(var(--destructive))", icon: OctagonX,     accent: "sev-critical" },
};

const fmtInt = (n) => Number(n ?? 0).toLocaleString("en-IN");
const fmtSec = (s) => {
  const v = Number(s ?? 0);
  if (v < 60) return `${Math.round(v)}s`;
  const m = Math.floor(v / 60);
  return `${m}m ${Math.round(v - m * 60)}s`;
};

const QUEUE_STATUS_COLOR = {
  Clear:    "hsl(var(--success))",
  Light:    "hsl(var(--primary))",
  Heavy:    "hsl(var(--warning))",
  Critical: "hsl(var(--destructive))",
};

const Pill = ({ icon: Icon, label, value, tone = "default", sub, testid }) => {
  const map = {
    default: "text-foreground",
    success: "sev-low",
    danger:  "sev-critical",
    warn:    "sev-medium",
    primary: "text-[hsl(var(--primary))]",
  };
  return (
    <div className="panel-elevated p-2.5 flex items-center gap-2" data-testid={testid}>
      <Icon className={`w-4 h-4 shrink-0 ${map[tone]}`} />
      <div className="min-w-0">
        <div className="micro-label leading-none">{label}</div>
        <div className={`font-display font-black text-lg leading-tight ${map[tone]}`} data-testid={`${testid}-value`}>
          {value}
        </div>
        {sub && <div className="micro-label text-[9px] mt-0.5 text-muted-foreground">{sub}</div>}
      </div>
    </div>
  );
};

const ProgressBar = ({ value, max = 1, color = "hsl(var(--primary))" }) => {
  const pct = Math.max(0, Math.min(1, value / max));
  return (
    <div className="h-1.5 bg-[hsl(var(--muted))] relative overflow-hidden">
      <div
        className="absolute inset-y-0 left-0 transition-all"
        style={{ width: `${pct * 100}%`, background: color }}
      />
    </div>
  );
};

const EmergencyRoutesBoard = ({ routes = [], onOpenIncident }) => (
  <div className="panel p-3" data-testid="ops-routes-board">
    <div className="flex items-center justify-between mb-2">
      <div className="h-widget flex items-center gap-1">
        <DoorOpen className="w-3 h-3" /> Emergency Route Status
      </div>
      <span className="micro-label">Click For Incidents</span>
    </div>
    {routes.length === 0 ? (
      <div className="text-[11px] text-muted-foreground py-4 text-center">No exit routes configured.</div>
    ) : (
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-2">
        {routes.map((r) => {
          const s = ROUTE_STATUS[r.status] || ROUTE_STATUS.Clear;
          const Icon = s.icon;
          return (
            <button
              key={r.id}
              onClick={onOpenIncident}
              data-testid={`ops-route-${r.id}`}
              className="text-left p-2.5 border-l-2 bg-[hsl(var(--surface-elevated))] hover:bg-[hsl(var(--surface))] transition-colors"
              style={{ borderLeftColor: s.color }}
            >
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-1.5 min-w-0">
                  <Icon className="w-3.5 h-3.5 shrink-0" style={{ color: s.color }} />
                  <span className="font-display font-bold text-xs truncate">{r.name}</span>
                </div>
                <span
                  className="px-1.5 py-0.5 text-[9px] font-bold tracking-wide border shrink-0"
                  style={{ color: s.color, borderColor: s.color }}
                >
                  {r.status}
                </span>
              </div>
              <div className="micro-label text-[9px] mt-1.5 text-muted-foreground truncate" title={r.reason}>
                {r.reason}
              </div>
              <div className="flex items-center justify-between mt-2 text-[10px] font-mono">
                <span>
                  <span className="text-muted-foreground">Flow</span>{" "}
                  <span className="text-foreground">{r.throughput}/min</span>
                </span>
                <span style={{ color: s.color }}>
                  {Math.round(r.utilization_pct * 100)}%
                </span>
              </div>
              <ProgressBar value={r.utilization_pct} color={s.color} />
              {r.alternative && (
                <div className="micro-label text-[9px] text-[hsl(var(--primary))] mt-1.5 truncate">
                  → Alternate: {r.alternative}
                </div>
              )}
            </button>
          );
        })}
      </div>
    )}
  </div>
);

const ExitMonitoringTable = ({ exits = [] }) => (
  <div className="panel p-3" data-testid="ops-exit-monitoring">
    <div className="flex items-center justify-between mb-2">
      <div className="h-widget">Exit Monitoring</div>
      <span className="micro-label">Real-Time Throughput</span>
    </div>
    {exits.length === 0 ? (
      <div className="text-[11px] text-muted-foreground py-4 text-center">No exits monitored.</div>
    ) : (
      <div className="overflow-x-auto">
        <table className="w-full text-[11px]">
          <thead>
            <tr className="text-left font-mono text-[10px] text-muted-foreground border-b border-border">
              <th className="py-1.5 px-2 font-medium">Exit</th>
              <th className="py-1.5 px-2 font-medium text-right">Flow /min</th>
              <th className="py-1.5 px-2 font-medium text-right">Capacity</th>
              <th className="py-1.5 px-2 font-medium">Utilization</th>
              <th className="py-1.5 px-2 font-medium text-right">ETA Clear</th>
              <th className="py-1.5 px-2 font-medium">Obstruction</th>
            </tr>
          </thead>
          <tbody>
            {exits.map((e) => {
              const s = ROUTE_STATUS[e.status] || ROUTE_STATUS.Clear;
              return (
                <tr
                  key={e.id}
                  className="border-b border-border last:border-b-0 hover:bg-[hsl(var(--surface-elevated))]"
                  data-testid={`ops-exit-row-${e.id}`}
                >
                  <td className="py-1.5 px-2 font-medium">{e.name}</td>
                  <td className="py-1.5 px-2 text-right font-mono">{e.throughput}</td>
                  <td className="py-1.5 px-2 text-right font-mono text-muted-foreground">{e.capacity}</td>
                  <td className="py-1.5 px-2" style={{ minWidth: 140 }}>
                    <div className="flex items-center gap-2">
                      <ProgressBar value={Math.min(1, e.utilization_pct)} color={s.color} />
                      <span className="font-mono text-[10px] w-9 text-right" style={{ color: s.color }}>
                        {Math.round(e.utilization_pct * 100)}%
                      </span>
                    </div>
                  </td>
                  <td className={`py-1.5 px-2 text-right font-mono ${e.eta_clear_sec > 0 ? "sev-medium" : "text-muted-foreground"}`}>
                    {e.eta_clear_sec > 0 ? fmtSec(e.eta_clear_sec) : "—"}
                  </td>
                  <td className="py-1.5 px-2">
                    {e.obstruction ? (
                      <span className="inline-flex items-center gap-1 sev-critical text-[10px] font-bold">
                        <AlertTriangle className="w-3 h-3" /> {e.obstruction_count}
                      </span>
                    ) : (
                      <span className="text-muted-foreground text-[10px]">None</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    )}
  </div>
);

const QueueAnalyticsTable = ({ rows = [] }) => (
  <div className="panel p-3" data-testid="ops-queue-table">
    <div className="flex items-center justify-between mb-2">
      <div className="h-widget flex items-center gap-1">
        <Users className="w-3 h-3" /> Queue Analytics
      </div>
      <span className="micro-label">Per Gate</span>
    </div>
    {rows.length === 0 ? (
      <div className="text-[11px] text-muted-foreground py-4 text-center">No gate queue data.</div>
    ) : (
      <div className="overflow-x-auto">
        <table className="w-full text-[11px]">
          <thead>
            <tr className="text-left font-mono text-[10px] text-muted-foreground border-b border-border">
              <th className="py-1.5 px-2 font-medium">Gate</th>
              <th className="py-1.5 px-2 font-medium text-right">Rate /min</th>
              <th className="py-1.5 px-2 font-medium text-right">Queue</th>
              <th className="py-1.5 px-2 font-medium text-right">Wait</th>
              <th className="py-1.5 px-2 font-medium">Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr
                key={r.gate_id}
                className="border-b border-border last:border-b-0 hover:bg-[hsl(var(--surface-elevated))]"
                data-testid={`ops-queue-row-${r.gate_id}`}
              >
                <td className="py-1.5 px-2 font-medium">{r.name}</td>
                <td className="py-1.5 px-2 text-right font-mono">{r.rate}</td>
                <td className="py-1.5 px-2 text-right font-mono">{r.queue}</td>
                <td className="py-1.5 px-2 text-right font-mono">{fmtSec(r.wait_sec)}</td>
                <td className="py-1.5 px-2">
                  <span
                    className="px-1.5 py-0.5 text-[9px] font-bold tracking-wide border"
                    style={{
                      color: QUEUE_STATUS_COLOR[r.status] || "hsl(var(--muted-foreground))",
                      borderColor: QUEUE_STATUS_COLOR[r.status] || "hsl(var(--border))",
                    }}
                  >
                    {r.status}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </div>
);

const ParkingOccupancyCard = ({ lots = [] }) => {
  const summary = useMemo(() => {
    const total = lots.reduce((s, l) => s + (l.capacity || 0), 0);
    const occ   = lots.reduce((s, l) => s + (l.vehicles_in_lot || 0), 0);
    return { total, occ, pct: total ? occ / total : 0 };
  }, [lots]);

  return (
    <div className="panel p-3" data-testid="ops-parking-card">
      <div className="flex items-center justify-between mb-2">
        <div className="h-widget flex items-center gap-1">
          <Car className="w-3 h-3" /> Parking Occupancy
        </div>
        <span className="micro-label">{fmtInt(summary.occ)} / {fmtInt(summary.total)}</span>
      </div>
      {lots.length === 0 ? (
        <div className="text-[11px] text-muted-foreground py-4 text-center">No parking data.</div>
      ) : (
        <div className="space-y-1.5">
          {lots.map((l) => {
            const pct = l.pct ?? 0;
            const color =
              pct >= 0.9 ? "hsl(var(--destructive))"
              : pct >= 0.75 ? "hsl(var(--warning))"
              : pct >= 0.55 ? "hsl(var(--primary))"
              : "hsl(217 91% 60%)";
            return (
              <div key={l.zone_id} className="text-[11px]" data-testid={`ops-parking-${l.zone_id}`}>
                <div className="flex items-center justify-between mb-0.5">
                  <span className="truncate">{l.name}</span>
                  <span className="font-mono text-[10px] text-muted-foreground">
                    {fmtInt(l.vehicles_in_lot)} / {fmtInt(l.capacity)} · {Math.round(pct * 100)}%
                  </span>
                </div>
                <ProgressBar value={pct} color={color} />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export const OperationalTab = ({ operational, gateQueues = [], parkingLots = [], onOpenIncident }) => {
  const op = operational || { kpis: {}, routes: [], exits: [] };
  const k = op.kpis || {};

  return (
    <div className="p-3 sm:p-4 space-y-3" data-testid="ops-tab">
      {/* KPI strip */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
        <Pill icon={DoorOpen}     label="Total Exits"   value={fmtInt(k.total_exits)}                          tone="primary" testid="ops-kpi-total" />
        <Pill icon={CheckCircle2} label="Clear"         value={fmtInt(k.clear)}                                tone="success" testid="ops-kpi-clear" />
        <Pill icon={AlertTriangle} label="Restricted"   value={fmtInt(k.restricted)}                           tone="warn"    testid="ops-kpi-restricted" />
        <Pill icon={OctagonX}     label="Blocked"       value={fmtInt(k.blocked)}                              tone="danger"  testid="ops-kpi-blocked" />
        <Pill icon={Timer}        label="Obstructions"  value={fmtInt(k.obstructions)}                         tone="warn"    testid="ops-kpi-obstr" />
        <Pill icon={TrendingUp}   label="Avg Flow"      value={`${Math.round((k.avg_utilization ?? 0) * 100)}%`} tone="default" testid="ops-kpi-flow" />
      </div>

      {/* Emergency routes — full width, hero panel */}
      <EmergencyRoutesBoard routes={op.routes} onOpenIncident={onOpenIncident} />

      {/* Exit monitoring */}
      <ExitMonitoringTable exits={op.exits} />

      {/* Queue + Parking */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <QueueAnalyticsTable rows={gateQueues} />
        <ParkingOccupancyCard lots={parkingLots} />
      </div>
    </div>
  );
};

export default OperationalTab;
